// SVG → PNG rasterizer. Used by the Shell / Floor Deformation pages to
// capture each scanline-profile chart at a fixed pixel size (e.g.
// 800 × 2000 px ≈ 8 × 20 cm at 100 px/cm) on Save-and-close so the
// inspection-detail page can show the image and a future PDF can
// embed it without further re-rendering.
//
// The defo charts use CSS variables (`--text-muted`, `--primary`,
// ...) for fill/stroke/font colours. When you serialise an SVG and
// load it back through <img>, those variables resolve against the
// fresh document — i.e. they don't resolve at all, so the chart
// renders blank-black. We work around that by walking every node in
// the source SVG and copying its computed styles to inline `style`
// attributes before serialising.

const SVG_STYLE_PROPS = [
  'fill', 'fill-opacity',
  'stroke', 'stroke-width', 'stroke-dasharray', 'stroke-linecap', 'stroke-linejoin', 'stroke-opacity',
  'opacity',
  'font-family', 'font-size', 'font-weight', 'font-style',
  'text-anchor', 'dominant-baseline', 'alignment-baseline',
];

function inlineComputedStyles(srcSvg) {
  const cloned = srcSvg.cloneNode(true);
  const srcAll = [srcSvg, ...srcSvg.querySelectorAll('*')];
  const dstAll = [cloned, ...cloned.querySelectorAll('*')];
  for (let i = 0; i < srcAll.length; i++) {
    const computed = window.getComputedStyle(srcAll[i]);
    let inline = '';
    for (const prop of SVG_STYLE_PROPS) {
      const v = computed.getPropertyValue(prop);
      if (v && v !== 'normal' && v !== 'auto') {
        inline += `${prop}:${v};`;
      }
    }
    if (inline) dstAll[i].setAttribute('style', inline);
    // Drop class so external CSS doesn't double-up; inline styles win.
    dstAll[i].removeAttribute('class');
  }
  return cloned;
}

export async function svgToPng(svgEl, widthPx, heightPx, background = '#ffffff') {
  if (!svgEl) throw new Error('svgToPng: no svg element');

  const cloned = inlineComputedStyles(svgEl);
  cloned.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  cloned.setAttribute('width', String(widthPx));
  cloned.setAttribute('height', String(heightPx));
  // Preserve the original viewBox so coordinates inside the SVG keep
  // mapping correctly across the new pixel dimensions.
  if (!cloned.getAttribute('viewBox') && svgEl.viewBox?.baseVal) {
    const v = svgEl.viewBox.baseVal;
    cloned.setAttribute('viewBox', `${v.x} ${v.y} ${v.width} ${v.height}`);
  }

  const svgString = new XMLSerializer().serializeToString(cloned);
  const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  try {
    const img = new Image();
    img.decoding = 'async';
    await new Promise((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = (e) => reject(new Error('SVG image load failed'));
      img.src = url;
    });

    const canvas = document.createElement('canvas');
    canvas.width = widthPx;
    canvas.height = heightPx;
    const ctx = canvas.getContext('2d');
    if (background) {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, widthPx, heightPx);
    }
    ctx.drawImage(img, 0, 0, widthPx, heightPx);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(url);
  }
}

// Strip the `data:image/png;base64,` prefix so the value can be sent as a
// plain base64 string in JSON. Backend accepts either form, but the
// raw form is ~30 % smaller on the wire.
export function stripDataUrlPrefix(dataUrl) {
  if (!dataUrl) return '';
  const comma = dataUrl.indexOf(',');
  return comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
}
